import React, { useEffect, useState } from "react";
import { InfoCard } from "../components/InfoCard";
import { NoticeSummery } from "../components/NoticeSummery";
import { useTutorial } from "../context/TutorialContext";

import {
  FaRegCalendar,
  FaRegClock,
  FaFileAlt,
  FaPen,
  FaPlus,
  FaFileImport,
} from "react-icons/fa";
import { Link } from "react-router-dom";
import TenantLink from "../components/TenantLink";
import { useAxiosPost } from "../hooks/useAxios";
import { CardTypes } from "../utils/enums";
import { Breadcrumb, Button, Typography, Tag, Modal } from "antd";
import { getCardsData, getNoticeDetails } from "../api/apiService";
import Calender from "../components/Calender";
import useTenantNavigate from "../hooks/useTenantNavigate";
import {
  ACTIVE_CLIENTS_TEXT,
  DISABLED_CLIENTS_TEXT,
  DUE_7_DAYS_TEXT,
  FAILED_LOGIN_TEXT,
  LAST_24_hours_TEXT,
  OPEN_NOTICES_TEXT,
  OVER_DUE_NOTICES_TEXT,
  TOTAL_NOTICE_TEXT,
  APPEAL_FEATURE_ENABLED,
} from "../utils/constants";
import AddClientModal from "../components/AddClientModal";
import BulkAssignDialog from "../components/BulkAssignDialog";
import { toast } from "react-toastify";
import { getClientDocType } from "../utils/helpers";
import { CLIENTS, DASHBOARD } from "../api/endpointMap";
import moment from "moment";
import {
  FileTextOutlined,
  SyncOutlined,
  TeamOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";
import { useAuth } from "../context/AuthContext";
import { useEmbedToken } from "../context/EmbedTokenContext";

// Absolute paths — same pattern as IncomeTax.jsx:256 and Tds.jsx:223.
// Relative `notices?type=...` from this index route was resolving to
// `/notices?type=...` (sibling of /gstin) and hitting the global 404.
const Cards = [
  {
    id: CardTypes.TOTAL_NOTICES,
    title: TOTAL_NOTICE_TEXT,
    path: "/gstin/notices?type=total_notices",
    icon: FaFileAlt,
    tutorialId: "total-notices",
  },
  {
    id: CardTypes.LAST_24_HOURS,
    title: LAST_24_hours_TEXT,
    path: "/gstin/notices?type=last_3_days_notices",
    icon: FaRegClock,
    tutorialId: "recent-notices",
  },
  {
    id: CardTypes.OPEN_NOTICES,
    title: OPEN_NOTICES_TEXT,
    path: "/gstin/notices?type=open_notices",
    icon: FaFileAlt,
    tutorialId: "open-notices",
  },
  {
    id: CardTypes.OVER_DUE,
    title: OVER_DUE_NOTICES_TEXT,
    path: "/gstin/notices?type=over_due_notices",
    icon: FaFileAlt,
    tutorialId: "overdue-notices",
  },
  {
    id: CardTypes.OVER_DUE_7_DAYS,
    title: DUE_7_DAYS_TEXT,
    path: "/gstin/notices?type=due_7_days_notices",
    icon: FaRegCalendar,
    tutorialId: "due-7-days",
  },
  {
    id: CardTypes.FAILED_LOGIN,
    title: FAILED_LOGIN_TEXT,
    path: "/gstin/notices?type=failed_login",
    icon: FaRegCalendar,
    tutorialId: "failed-login",
  },
  {
    id: CardTypes.ACTIVE_CLIENTS,
    title: ACTIVE_CLIENTS_TEXT,
    path: "/gstin/clients?type=active",
    icon: FaFileAlt,
    tutorialId: "active-clients",
  },
  {
    id: CardTypes.DISABLE_CLIENTS,
    title: DISABLED_CLIENTS_TEXT,
    path: "/gstin/clients?type=inactive",
    icon: FaFileAlt,
    tutorialId: "inactive-clients",
  },
];

const { Text, Title } = Typography;

export const Gstin = () => {
  const navigate = useTenantNavigate();
  const { user: currentUser } = useAuth();
  // R71 — Hide Add / Import affordances inside /embed/* iframe (Zoho
  // partner syncs clients directly via partner API).
  const { claims: embedClaims } = useEmbedToken();
  const isTenantAdmin =
    currentUser?.role === "tenant_admin" ||
    currentUser?.role === "super_admin";
  const [cardsData, setCardsData] = useState({});
  const [openClientModal, setOpenClientModal] = useState(false);
  const [bulkAssignOpen, setBulkAssignOpen] = useState(false);
  const [addNewClient] = useAxiosPost(CLIENTS.CREATE_GST);
  const [loading, setLoading] = useState(true);
  const [checkingRunningImport, setCheckingRunningImport] = useState(false);
  const [runningImportModal, setRunningImportModal] = useState(false);
  const [runningImportData, setRunningImportData] = useState(null);

  const { setPage, startTutorialForPage, isFirstUser } = useTutorial();

  const getData = async () => {
    try {
      const data = await getCardsData(DASHBOARD.GST_CARDS);
      setCardsData(data?.data || {});
    } catch (err) {
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    }
  };

  const handleAddClient = async (payload) => {
    try {
      addNewClient({
        payload,
        cb: (data, msg) => {
          if (data == null) {
            toast.error(msg ?? "Error in creating client");
            setOpenClientModal(false);
          } else {
            setOpenClientModal(false);
            toast.info("Added Client Successfully", {
              position: "top-right",
              autoClose: 5000,
            });
            navigate("/gstin/clients?type=active");
          }
        },
      });
    } catch (err) {
      toast.error(err);
    }
  };

  const checkRunningImport = async () => {
    setCheckingRunningImport(true);
    try {
      const doctype = getClientDocType("gstin");
      // Legacy `POST /api/v1/clients/import/status` with {doctype} returns 405
      // on FastAPI — that path is GET-only and expects `data_import_id`.
      // Use working `GET /api/v1/clients/import/last?doctype=<X>` which
      // returns the most recent import for this doctype; if its status is
      // Pending/Processing, a run is in flight and we show the modal.
      // (Mirror of IncomeTax.jsx:88-130 — same pattern, same comment.)
      const response = await getNoticeDetails(
        `/api/v1/clients/import/last?doctype=${encodeURIComponent(doctype)}`
      );
      const lastImport = response?.message?.data;
      const isRunning =
        lastImport && ["Pending", "Processing"].includes(lastImport.status);

      if (isRunning) {
        setTimeout(() => {
          // Shape to what the modal renders below:
          // runningImportData.imports[].{status, data_import, user, started_at}
          setRunningImportData({
            imports: [
              {
                status: lastImport.status,
                data_import: lastImport.file_name || lastImport.id,
                user: "-",
                started_at: lastImport.created_at,
              },
            ],
          });
          setRunningImportModal(true);
        }, 1000);
      } else {
        setTimeout(() => {
          navigate("/gstin/clients?type=active&import=true");
        }, 1000);
      }
    } catch (err) {
      console.error("Error checking running import:", err);
      toast.error("Failed to check import status. Please try again.");
    } finally {
      setTimeout(() => {
        setCheckingRunningImport(false);
      }, 1000);
    }
  };

  useEffect(() => {
    getData();
    setPage("gstin");

    if (isFirstUser) {
      const timer = setTimeout(() => {
        startTutorialForPage("gstin");
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  return (
    <div className="p-6 bg-gray-100" data-tutorial="gst-main">
      <div className="relative flex flex-col gap-3 xl:flex-row xl:justify-between xl:items-start xl:gap-0 w-full">
        <div className="flex items-center gap-4">
          <Breadcrumb
            items={[
              {
                title: (
                  <TenantLink
                    className="exempt-from-readonly"
                    to={{
                      pathname: "/",
                    }}
                  >
                    Home
                  </TenantLink>
                ),
              },
              {
                title: "GST",
              },
            ]}
            style={{
              marginBottom: "10px",
              color: "#151c40",
            }}
          />
        </div>

        {/* "Prepare Appeal" promoted CTA (approved 2026-07-09), centred
            horizontally like the navbar's NoticeAI logo (user request
            11-07-2026). RESPONSIVE layout, three modes:
            - below xl: the header stacks — breadcrumb, then this button
              CENTRED on its own line, then the action buttons in centred
              wrapped rows. Clean mobile composition, nothing overlaps.
            - xl to 2xl: single row; the button is an in-flow middle flex
              item between breadcrumb and actions (centred in the free
              space — in-flow items cannot slide under the buttons).
            - 2xl+ (>=1536px): absolute page-centre — mathematically clear
              of both clusters, aligning EXACTLY with the navbar logo.
            Gradient + NEW badge styling keeps it reading as a fresh
            capability. Still gated behind `!embedClaims` — partner embeds
            don't surface appeal drafting. Also hidden when the Appeal feature
            is globally disabled (production hold). */}
        {APPEAL_FEATURE_ENABLED && !embedClaims && (
          <div className="flex justify-center xl:-mt-1 xl:flex-1 xl:px-2 2xl:flex-none 2xl:px-0 2xl:absolute 2xl:left-1/2 2xl:transform 2xl:-translate-x-1/2">
            <button
              type="button"
              onClick={() => navigate("/gstin/appeals")}
              data-tutorial="prepare-appeal"
              className="relative inline-flex items-center gap-2 px-4 py-2 rounded-lg font-semibold text-white transition-all duration-200 hover:scale-[1.03]"
              style={{
                // Theme navy gradient: matches the app's #1e3a8a primary
                // and #2746b2 hover so the button reads as promoted
                // without breaking the palette. Shadow uses the same
                // navy tone (rgba of #1e3a8a) for a subtle glow.
                background:
                  "linear-gradient(135deg, #1e3a8a 0%, #2746b2 100%)",
                boxShadow: "0 4px 12px rgba(30, 58, 138, 0.35)",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow =
                  "0 6px 18px rgba(30, 58, 138, 0.5)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow =
                  "0 4px 12px rgba(30, 58, 138, 0.35)";
              }}
            >
              <ThunderboltOutlined style={{ fontSize: 16 }} />
              <span>Prepare Appeal</span>
              <span
                className="absolute -top-2 -right-3 text-[10px] font-bold px-1.5 py-0.5 rounded-full shadow-md animate-pulse"
                style={{
                  letterSpacing: "0.5px",
                  background: "#fff",
                  color: "#1e3a8a",
                  border: "1px solid #1e3a8a",
                }}
              >
                NEW
              </span>
            </button>
          </div>
        )}

        {/* Action cluster (user request 11-07-2026): centred wrapped rows
            below xl (stacked header), right-anchored single row from xl up
            (parent row + justify-between restores the desktop layout). */}
        <div
          className="flex flex-wrap gap-2 justify-center xl:justify-end xl:gap-4"
          data-tutorial="client-buttons"
        >
          {!embedClaims && (
            <>
              <Button
                onClick={() => {
                  setOpenClientModal(true);
                }}
                variant="outlined"
                icon={<FaPlus />}
                type="primary"
                data-tutorial="add-client"
              >
                Add GST Client
              </Button>
              <Button
                data-tutorial="import-client"
                onClick={() => {
                  checkRunningImport();
                }}
                variant="outlined"
                icon={<FaFileImport />}
                type="primary"
                loading={checkingRunningImport}
              >
                Import Client
              </Button>
            </>
          )}
          <Button
            onClick={() => {
              navigate("/gstin/clients?type=active");
            }}
            variant="outlined"
            icon={<FaPen />}
            type="primary"
            data-tutorial="edit-client"
          >
            Edit Client
          </Button>
          <Button
            onClick={() => setBulkAssignOpen(true)}
            variant="outlined"
            icon={<TeamOutlined />}
            type="primary"
            style={isTenantAdmin ? undefined : { display: "none" }}
          >
            Bulk Assign
          </Button>
        </div>
      </div>

      <h2 className="text-xl font-bold text-blue-900 mb-4">GST</h2>
      <div className="flex flex-col gap-4 md:flex-row md:gap-4">
        <div className="w-full md:w-1/2">
          {/* Cards Section */}
          <div className="grid grid-cols-1 md:grid-cols-1 gap-6 mb-[20px]">
            <div
              className="grid grid-cols-2 lg:grid-cols-2 gap-4 mb-6"
              data-tutorial="notice-cards"
            >
              {Cards.map((card, index) => (
                <div key={index} data-tutorial={card.tutorialId}>
                  <InfoCard
                    title={card.title}
                    loading={loading}
                    count={cardsData[card.id] || 0}
                    icon={<card.icon />}
                    onClick={() => {
                      if (card.path) {
                        navigate(card.path);
                      }
                    }}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="w-full md:w-1/2" data-tutorial="gst-calendar">
          <Calender portal={"GST"} />
        </div>
      </div>

      <Modal
        title={
          <div className="flex items-center gap-2">
            <SyncOutlined spin style={{ color: "#1890ff", fontSize: "20px" }} />
            <span>Import Already in Progress</span>
          </div>
        }
        open={runningImportModal}
        onCancel={() => {
          setRunningImportModal(false);
          setRunningImportData(null);
        }}
        footer={[
          <Button
            key="close"
            type="primary"
            onClick={() => {
              setRunningImportModal(false);
              setRunningImportData(null);
            }}
          >
            OK, I'll Wait
          </Button>,
        ]}
        width={600}
        centered
      >
        <div className="py-4">
          <div className="text-center mb-4">
            <div className="mb-0">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-100 mb-4">
                <FileTextOutlined
                  style={{ fontSize: "32px", color: "#1890ff" }}
                />
              </div>
            </div>
            <Title level={4} style={{ color: "#1890ff", marginBottom: "5px" }}>
              Client Import Currently Running
            </Title>
            <Text type="secondary" style={{ fontSize: "15px" }}>
              Another client import process is currently running. Please wait
              for it to complete before starting a new import.
            </Text>
          </div>

          {runningImportData?.imports &&
            runningImportData.imports.length > 0 && (
              <div className="mb-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 overflow-hidden h-50">
                  <Text
                    strong
                    className="block mb-3"
                    style={{ color: "#1890ff" }}
                  >
                    Running Import Details:
                  </Text>
                  {runningImportData.imports
                    .slice(0, 1)
                    .map((importItem, index) => (
                      <div
                        key={index}
                        className="bg-white rounded-md p-3 mb-2 border border-blue-100"
                        style={{ fontSize: "14px" }}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-semibold text-gray-700">
                            Import Name:
                          </span>
                          <Tag color="blue" icon={<SyncOutlined spin />}>
                            {importItem.status || "Running"}
                          </Tag>
                        </div>
                        <div className="space-y-1 text-gray-600">
                          <div className="flex justify-between">
                            <span>Data Import:</span>
                            <span className="font-medium text-gray-800">
                              {importItem.data_import}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span>Started By:</span>
                            <span className="font-medium text-gray-800">
                              {importItem.user}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span>Started At:</span>
                            <span className="font-medium text-gray-800">
                              {moment(importItem.started_at).format(
                                "MMMM Do YYYY"
                              )}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            )}

          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-md">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-yellow-400"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <Text className="text-yellow-800">
                  <strong>Please Note:</strong> You can refresh this page or try
                  again in a few minutes. The running import must complete
                  before you can start a new one.
                </Text>
              </div>
            </div>
          </div>
        </div>
      </Modal>

      <AddClientModal
        open={openClientModal}
        handleClose={() => setOpenClientModal(false)}
        handleSubmit={handleAddClient}
        type={"GST"}
        title={"GST"}
      />

      <BulkAssignDialog
        open={bulkAssignOpen}
        onClose={() => setBulkAssignOpen(false)}
        onSuccess={getData}
        portal="gst"
      />
    </div>
  );
};
